import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Modal from "react-bootstrap/Modal";
import Swal from "sweetalert2";
import { showApiKeyCopyDialog } from "../../utility/api-key-copy-dialog";
import partnerApiKeyService from "../../services/partner-api-key.service";

const EditPartnerSoftwareModal = ({ show, onHide, record, isCreate }) => {
  const [regenerating, setRegenerating] = useState(false);
  const [keyPrefix, setKeyPrefix] = useState(record?.prefix || "");

  useEffect(() => {
    if (show && record?.prefix) {
      setKeyPrefix(record.prefix);
    }
  }, [show, record?.prefix]);

  const handleRegenerateKey = async () => {
    if (!record?.id) return;
    const confirm = await Swal.fire({
      title: "Regenerate API key?",
      html: `<p>The current key (<code>${keyPrefix}</code>…) will <strong>stop working immediately</strong>.</p>
        <p class="mb-0">The partner must update their integration with the new key. All other settings are unchanged.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Regenerate",
      confirmButtonColor: "#d33",
    });
    if (!confirm.isConfirmed) return;

    setRegenerating(true);
    const result = await partnerApiKeyService.Regenerate(record.id);
    setRegenerating(false);

    if (result.success && result.data?.apiKey) {
      setKeyPrefix(result.data.prefix);
      await showApiKeyCopyDialog({
        title: "New API key",
        bodyHtml: `<p class="text-danger"><strong>Store this key now</strong> — it cannot be retrieved again. The old key is revoked.</p>`,
        apiKey: result.data.apiKey,
        prefix: result.data.prefix,
      });
    }
  };

  const validationSchema = Yup.object({
    clientName: Yup.string().required("Platform name is required"),
    clientEmail: Yup.string().email("Invalid email").nullable(),
    description: Yup.string().nullable(),
    rateLimit: Yup.number().min(1).max(10000).nullable(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      clientName: record?.clientName || "",
      clientEmail: record?.clientEmail || "",
      description: record?.description || "",
      rateLimit: record?.rateLimit ?? 100,
      expiresAt: record?.expiresAt
        ? new Date(record.expiresAt).toISOString().slice(0, 16)
        : "",
      isActive: record?.isActive !== false,
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (isCreate) {
        const payload = {
          clientName: values.clientName.trim(),
          clientEmail: values.clientEmail?.trim() || undefined,
          description: values.description?.trim() || undefined,
          rateLimit: Number(values.rateLimit) || 100,
          expiresAt: values.expiresAt
            ? new Date(values.expiresAt).toISOString()
            : undefined,
        };
        const result = await partnerApiKeyService.Create(payload);
        if (result.success && result.data?.apiKey) {
          await showApiKeyCopyDialog({
            title: "API key created",
            bodyHtml: `<p>Store this key securely — it cannot be retrieved again.</p>`,
            apiKey: result.data.apiKey,
            prefix: result.data.prefix,
          });
          onHide(true);
        }
      } else if (record?.id) {
        const updateResult = await partnerApiKeyService.Update(record.id, {
          clientName: values.clientName.trim(),
          clientEmail: values.clientEmail?.trim() || null,
          description: values.description?.trim() || null,
          rateLimit: Number(values.rateLimit) || 100,
          isActive: values.isActive,
          expiresAt: values.expiresAt
            ? new Date(values.expiresAt).toISOString()
            : null,
        });

        if (!updateResult.success) {
          setSubmitting(false);
          return;
        }

        onHide(true, record.id);
      }
      setSubmitting(false);
    },
  });

  return (
    <Modal show={show} onHide={() => onHide(false)} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          {isCreate ? "Add partner platform" : `Edit — ${record?.clientName || ""}`}
        </Modal.Title>
      </Modal.Header>
      <form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Platform name *</label>
              <input
                type="text"
                className={`form-control ${formik.touched.clientName && formik.errors.clientName ? "is-invalid" : ""}`}
                name="clientName"
                value={formik.values.clientName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="e.g. HealthFirst Pharmacy"
              />
              {formik.touched.clientName && formik.errors.clientName && (
                <div className="invalid-feedback">{formik.errors.clientName}</div>
              )}
            </div>
            <div className="col-md-6">
              <label className="form-label">Contact email</label>
              <input
                type="email"
                className={`form-control ${formik.touched.clientEmail && formik.errors.clientEmail ? "is-invalid" : ""}`}
                name="clientEmail"
                value={formik.values.clientEmail}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>
            <div className="col-12">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                name="description"
                rows={2}
                value={formik.values.description}
                onChange={formik.handleChange}
                placeholder="Integration purpose or notes"
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Rate limit (req/min)</label>
              <input
                type="number"
                className="form-control"
                name="rateLimit"
                min={1}
                value={formik.values.rateLimit}
                onChange={formik.handleChange}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Expires at</label>
              <input
                type="datetime-local"
                className="form-control"
                name="expiresAt"
                value={formik.values.expiresAt}
                onChange={formik.handleChange}
              />
            </div>
            {!isCreate && (
              <div className="col-md-4 d-flex align-items-end">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="isActive"
                    checked={formik.values.isActive}
                    onChange={(e) => formik.setFieldValue("isActive", e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="isActive">
                    Active
                  </label>
                </div>
              </div>
            )}
            {!isCreate && (
              <div className="col-12">
                <div className="border rounded p-3 bg-light">
                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                    <div>
                      <label className="form-label mb-1">API key</label>
                      <p className="mb-0 small text-muted">
                        Current prefix: <code>{keyPrefix || "—"}</code> (full key is not
                        stored)
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      disabled={regenerating || formik.isSubmitting}
                      onClick={handleRegenerateKey}
                    >
                      <i className="fa fa-rotate me-1"></i>
                      {regenerating ? "Regenerating…" : "Regenerate key"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={() => onHide(false)}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={formik.isSubmitting}>
            {isCreate ? "Create & generate API key" : "Save"}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default EditPartnerSoftwareModal;
